import React from 'react'
import { Box, Typography, styled, Avatar } from '@mui/material'
import { Person, GridView, CreditCard, Settings, Security, ChevronRight } from '@mui/icons-material'
import { ProfileTab } from '../../ModernProfilePage'
import useAppStore from '@/store/useAppStore'
import { shallow } from 'zustand/shallow'

// ==================== TYPES ====================
interface ModernProfileSidebarProps {
  activeTab: ProfileTab
  onTabChange: (tab: ProfileTab) => void
}

interface NavItem {
  id: ProfileTab
  label: string
  icon: React.ReactNode
  description: string
}

// ==================== STYLED COMPONENTS ====================
const SidebarContainer = styled(Box)(({ theme }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  width: '280px',
  height: '100vh',
  backgroundColor: '#ffffff',
  borderRight: '1px solid #e5e7eb',
  display: 'flex',
  flexDirection: 'column',
  zIndex: 100,

  [theme.breakpoints.down('md')]: {
    display: 'none', // Hide on mobile, can add hamburger menu later
  },
}))

const SidebarHeader = styled(Box)({
  padding: '24px 20px',
  borderBottom: '1px solid #e5e7eb',
})

const UserInfo = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  padding: '12px',
  borderRadius: '12px',
  cursor: 'pointer',
  transition: 'all 0.2s ease',

  '&:hover': {
    backgroundColor: '#f9fafb',
  },
})

const NavSection = styled(Box)({
  padding: '16px 12px',
  flex: 1,
  overflowY: 'auto',

  '&::-webkit-scrollbar': {
    width: '6px',
  },
  '&::-webkit-scrollbar-track': {
    background: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    background: '#d1d5db',
    borderRadius: '3px',
    '&:hover': {
      background: '#9ca3af',
    },
  },
})

const NavItem = styled(Box)<{ active?: boolean }>(({ active }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  padding: '12px 16px',
  borderRadius: '10px',
  marginBottom: '4px',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  position: 'relative',
  backgroundColor: active ? '#f3f4f6' : 'transparent',

  '&::before': {
    content: '""',
    position: 'absolute',
    left: 0,
    top: '50%',
    transform: 'translateY(-50%)',
    width: '3px',
    height: active ? '20px' : '0',
    backgroundColor: '#3b82f6',
    borderRadius: '0 2px 2px 0',
    transition: 'height 0.2s ease',
  },

  '&:hover': {
    backgroundColor: active ? '#f3f4f6' : '#f9fafb',
  },

  '& .nav-icon': {
    fontSize: '20px',
    color: active ? '#3b82f6' : '#6b7280',
    transition: 'color 0.2s ease',
  },

  '& .nav-arrow': {
    fontSize: '16px',
    color: '#9ca3af',
    marginLeft: 'auto',
    opacity: active ? 1 : 0,
    transform: active ? 'translateX(0)' : 'translateX(-4px)',
    transition: 'all 0.2s ease',
  },

  '&:hover .nav-arrow': {
    opacity: 1,
    transform: 'translateX(0)',
  },
}))

const SidebarFooter = styled(Box)({
  padding: '16px 20px',
  borderTop: '1px solid #e5e7eb',
})

// ==================== NAV ITEMS ====================
const navItems: NavItem[] = [
  {
    id: 'profile',
    label: 'Profile',
    icon: <Person className='nav-icon' />,
    description: 'Public profile and settings',
  },
  {
    id: 'content',
    label: 'Content',
    icon: <GridView className='nav-icon' />,
    description: 'Patterns, collections, and uploads',
  },
  {
    id: 'billing',
    label: 'Billing',
    icon: <CreditCard className='nav-icon' />,
    description: 'Plans and payment',
  },
  {
    id: 'account',
    label: 'Account',
    icon: <Settings className='nav-icon' />,
    description: 'Email and password',
  },
  {
    id: 'security',
    label: 'Security',
    icon: <Security className='nav-icon' />,
    description: 'Privacy and data',
  },
]

// ==================== COMPONENT ====================
const ModernProfileSidebar: React.FC<ModernProfileSidebarProps> = ({ activeTab, onTabChange }) => {
  const [userProfile] = useAppStore((state) => [state.user.profile], shallow)

  return (
    <SidebarContainer>
      {/* Header with User Info */}
      <SidebarHeader>
        <Typography
          sx={{
            fontSize: '20px',
            fontWeight: 700,
            color: '#111827',
            letterSpacing: '-0.02em',
            mb: 2,
          }}
        >
          Settings
        </Typography>

        <UserInfo>
          <Avatar
            src={userProfile?.profilePicThumbURL}
            alt={userProfile?.userName}
            sx={{
              width: 40,
              height: 40,
              border: '2px solid #e5e7eb',
            }}
          >
            {userProfile?.userName?.charAt(0)?.toUpperCase()}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: '14px',
                fontWeight: 600,
                color: '#111827',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {userProfile?.userName || 'User'}
            </Typography>
            <Typography
              sx={{
                fontSize: '12px',
                fontWeight: 500,
                color: '#6b7280',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {userProfile?.userEmail || 'user@example.com'}
            </Typography>
          </Box>
        </UserInfo>
      </SidebarHeader>

      {/* Navigation */}
      <NavSection>
        <Typography
          sx={{
            fontSize: '11px',
            fontWeight: 600,
            color: '#9ca3af',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            px: 2,
            mb: 1,
          }}
        >
          Navigation
        </Typography>

        {navItems.map((item) => (
          <NavItem
            key={item.id}
            active={activeTab === item.id}
            onClick={() => onTabChange(item.id)}
          >
            {item.icon}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: '14px',
                  fontWeight: 600,
                  color: activeTab === item.id ? '#111827' : '#4b5563',
                  lineHeight: 1.2,
                }}
              >
                {item.label}
              </Typography>
              <Typography
                sx={{
                  fontSize: '11px',
                  fontWeight: 500,
                  color: '#9ca3af',
                  lineHeight: 1.3,
                  mt: 0.25,
                }}
              >
                {item.description}
              </Typography>
            </Box>
            <ChevronRight className='nav-arrow' />
          </NavItem>
        ))}
      </NavSection>

      {/* Footer */}
      <SidebarFooter>
        <Typography
          sx={{
            fontSize: '11px',
            fontWeight: 500,
            color: '#9ca3af',
            textAlign: 'center',
          }}
        >
          Version 2.0.0
        </Typography>
      </SidebarFooter>
    </SidebarContainer>
  )
}

export default ModernProfileSidebar
